import styled from "styled-components";
import { CSSVars } from "@/styles/styles.config";

const STAR_COLOR = "#f58a8a";
const ACCENT_GRADIENT = `linear-gradient(270deg, ${STAR_COLOR} 0%, #a66cd9 50%, #5b5bff 100%)`;

export const ReviewStar = styled.span`
  display: inline-block;
  width: 20px;
  height: 20px;
  background: ${STAR_COLOR};
  -webkit-mask: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'><polygon points='12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26'/></svg>")
    center / contain no-repeat;
  mask: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'><polygon points='12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26'/></svg>")
    center / contain no-repeat;
`;

export const StyledSection = styled.section`
  background: linear-gradient(180deg, #ffffff 0%, #f8f9fc 100%);
  padding: 70px 20px;

  @media screen and (min-width: ${CSSVars.breakpointMD}) {
    padding: 90px 30px;
  }
  @media screen and (min-width: ${CSSVars.breakpointLG}) {
    padding: 110px 40px;
  }

  .inner {
    max-width: 1240px;
    margin: 0 auto;
  }

  /* ---------- Header ---------- */
  .head {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    margin-bottom: 48px;

    @media screen and (min-width: ${CSSVars.breakpointLG}) {
      margin-bottom: 64px;
    }
  }

  .eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: rgba(76, 79, 255, 0.08);
    color: ${CSSVars.blue500};
    font-family: ${CSSVars.primaryFont};
    font-weight: 600;
    font-size: 12px;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    padding: 6px 12px;
    border-radius: 999px;
    margin-bottom: 16px;
  }

  h2 {
    font-family: ${CSSVars.primaryFont};
    font-weight: 700;
    font-size: 30px;
    line-height: 1.15;
    color: ${CSSVars.black};
    margin: 0;
    max-width: 820px;
    letter-spacing: -0.02em;

    @media screen and (min-width: ${CSSVars.breakpointMD}) {
      font-size: 40px;
    }
    @media screen and (min-width: ${CSSVars.breakpointLG}) {
      font-size: 48px;
    }
  }

  /* ---------- Grid ---------- */
  .grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
    margin-bottom: 40px;

    @media screen and (min-width: ${CSSVars.breakpointMD}) {
      grid-template-columns: repeat(3, 1fr);
      gap: 20px;

      .featured {
        grid-column: 1 / -1;
      }
    }

    @media screen and (min-width: ${CSSVars.breakpointLG}) {
      gap: 24px;
    }
  }

  /* ---------- Cards ---------- */
  .card {
    background: ${CSSVars.white};
    border: 1px solid ${CSSVars.gray100};
    border-radius: 18px;
    padding: 24px 22px;
    display: flex;
    flex-direction: column;
    gap: 14px;
    box-shadow: 0 2px 10px rgba(16, 24, 40, 0.03);
    transition: box-shadow 0.25s ease, transform 0.25s ease,
      border-color 0.25s ease;
    position: relative;
    overflow: hidden;

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 3px;
      background: ${ACCENT_GRADIENT};
      opacity: 0;
      transition: opacity 0.25s ease;
    }

    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 14px 32px rgba(16, 24, 40, 0.08);
      border-color: ${CSSVars.gray200};

      &::before {
        opacity: 1;
      }
    }

    .card-top {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
    }

    .stars {
      display: inline-flex;
      gap: 2px;
    }

    .verified {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      font-family: ${CSSVars.primaryFont};
      font-size: 11px;
      font-weight: 600;
      color: ${CSSVars.blue500};
      letter-spacing: 0.2px;
    }

    .quote {
      font-family: ${CSSVars.secondaryFont};
      font-weight: 400;
      font-size: 15px;
      line-height: 1.65;
      color: #1f2937;
      margin: 0;
    }

    .context {
      font-family: ${CSSVars.secondaryFont};
      font-size: 13px;
      color: #6b7280;
      margin: 0;
      padding-top: 8px;
      border-top: 1px solid ${CSSVars.gray100};
    }
  }

  /* ---------- Featured card ---------- */
  .card.featured {
    background: linear-gradient(
        145deg,
        rgba(245, 138, 138, 0.06) 0%,
        rgba(166, 108, 217, 0.05) 50%,
        rgba(91, 91, 255, 0.06) 100%
      ),
      ${CSSVars.white};
    border: 1px solid rgba(91, 91, 255, 0.18);
    padding: 32px 28px;
    gap: 18px;

    &::before {
      opacity: 1;
      height: 4px;
    }

    @media screen and (min-width: ${CSSVars.breakpointMD}) {
      padding: 40px 36px;
    }

    .card-top .stars ${ReviewStar} {
      width: 24px;
      height: 24px;
    }

    .card-top .verified {
      font-size: 12px;
    }

    .quote {
      font-family: ${CSSVars.primaryFont};
      font-weight: 500;
      font-size: 20px;
      line-height: 1.5;
      color: ${CSSVars.black};
      letter-spacing: -0.005em;
      position: relative;

      @media screen and (min-width: ${CSSVars.breakpointMD}) {
        font-size: 24px;
      }
      @media screen and (min-width: ${CSSVars.breakpointLG}) {
        font-size: 26px;
      }

      .open-quote {
        font-family: ${CSSVars.primaryFont};
        font-size: 64px;
        font-weight: 700;
        line-height: 0.5;
        color: ${STAR_COLOR};
        opacity: 0.45;
        margin-right: 4px;
        vertical-align: -16px;
      }
    }
  }

  /* ---------- See all ---------- */
  .see-all {
    display: flex;
    align-items: center;
    gap: 8px;
    width: fit-content;
    margin: 0 auto;
    font-family: ${CSSVars.primaryFont};
    font-weight: 600;
    font-size: 15px;
    color: ${CSSVars.white};
    text-decoration: none;
    padding: 14px 26px;
    border-radius: 999px;
    border: none;
    background: ${ACCENT_GRADIENT};
    background-size: 200% 100%;
    background-position: 100% 0;
    transition: background-position 0.4s ease, transform 0.2s ease,
      box-shadow 0.2s ease;
    box-shadow: 0 4px 14px rgba(76, 79, 255, 0.25);

    &:hover {
      background-position: 0 0;
      transform: translateY(-1px);
      box-shadow: 0 8px 22px rgba(76, 79, 255, 0.32);
    }

    span {
      transition: transform 0.2s ease;
    }
    &:hover span {
      transform: translateX(3px);
    }
  }
`;
